// ---------- alphabet registry (drawing face) ----------
// Adding an alphabet = one file and one line here. Rendering, the legend and the help texts
// all draw from this registry.
//
// It knows nothing of conversion: the encoders have their own registry next door
// (encoders.ts), which the game never imports. That is the whole point of the split — the
// game plays grids the admin made, it does not make any.

import { asciiElargi } from './asciiElargi';
import { briques } from './briques';
import { briquesNuancees } from './briquesNuancees';
import { desPlein } from './desPlein';
import { hachuresPleines } from './hachuresPleines';
import { motifs } from './motifs';
import { motifsFin } from './motifsFin';
import { motifsTrait } from './motifsTrait';
import { peinture } from './peinture';
import { peintureNuancee } from './peintureNuancee';
import { pointille } from './pointille';
import { pointilleElargi } from './pointilleElargi';
import { pointilleRiche } from './pointilleRiche';
import { pointDeCroix } from './pointDeCroix';
import type { Alphabet } from './types';

// Retirés du registre : ascii, hachures et des (les versions "délavées", cf. les commits qui ont
// ajouté asciiElargi/hachuresPleines/desPlein pour les remplacer). Aucun puzzle publié ne les
// utilisait au moment du retrait (vérifié en base de prod) — sans quoi ce retrait aurait fait
// disparaître ces puzzles du catalogue de tout le monde, cf. tests/alphabets.test.ts.
export const ALPHABETS: readonly Alphabet[] = [
  motifs,
  motifsFin,
  motifsTrait,
  asciiElargi,
  pointille,
  pointilleElargi,
  pointilleRiche,
  hachuresPleines,
  desPlein,
  briques,
  peinture,
  peintureNuancee,
  briquesNuancees,
  pointDeCroix,
];

/** Number of codes per alphabet — the ceiling every alphabet may use; the palette, the
 *  audio and the cell encoding all depend on it. Set by the alphabet with the most codes
 *  (peintureNuancee, 0..256 : le papier et deux cent cinquante-six couleurs — c'était 40,
 *  pointilleRiche, avant lui): alphabets with fewer must declare an explicit `paletteOrder` of
 *  their own range, or codeOrder()'s NUMERIC fallback would inflate to this ceiling too.
 *
 *  À relever AVEC `MAX_SYMBOL` dans backend/src/validation.ts, et le serveur déployé AVANT le
 *  jeu : sinon une grille de peinture nuancée est refusée à l'enregistrement. */
export const NB_CODES = 257;

/** Code 0 is never in the palette: it is bare paper, not a symbol — resetPlay() marks its cells
 *  done from the start, so there is nothing to fill and nothing to lock onto. */
const NUMERIC: readonly number[] = Array.from({ length: NB_CODES - 1 }, (_, i) => i + 1);

const byId = new Map(ALPHABETS.map((a) => [a.id, a]));

/** The alphabet an id names, or the shapes one — which every reader can fall back on, since a
 *  grid whose alphabet is unknown is still a grid of codes. */
export function alphabetById(id: string): Alphabet {
  return byId.get(id) ?? motifs;
}

/**
 * Est-ce que CE build sait dessiner cet id ?
 *
 * Distinct de `alphabetById`, qui retombe toujours sur les motifs et ne peut donc pas répondre.
 * Le repli est le bon comportement quand il faut bien dessiner quelque chose ; il est trompeur
 * partout où il faut DÉCIDER — proposer une feuille dans le catalogue, ou déclarer au serveur ce
 * qu'on sait peindre. Une grille tirée d'un alphabet inconnu n'est pas jouable : ses codes
 * au-delà de ceux du repli n'ont pas de pastille, donc pas de moyen d'être peints.
 */
export function alphabetKnown(id: string): boolean {
  return byId.has(id);
}

/** Les ids que ce build sait dessiner, pour les déclarer au serveur (cf. src/api.ts). */
export function knownAlphabetIds(): string[] {
  return ALPHABETS.map((a) => a.id);
}

/** The order the palette reads its symbols in — see Alphabet.paletteOrder. */
export function codeOrder(a: Alphabet): readonly number[] {
  return a.paletteOrder ?? NUMERIC;
}

/** Position of each code in that order, indexed by code, for the loops that can't afford an
 *  indexOf per cell (a grid holds up to 14 400). Unused codes get a rank past the end. */
export function codeRank(a: Alphabet): readonly number[] {
  const rank = new Array<number>(NB_CODES).fill(NB_CODES);
  codeOrder(a).forEach((code, i) => {
    rank[code] = i;
  });
  return rank;
}

/**
 * Les cases non vides d'une grille, dans l'ordre où les DESSINER hors partie — couverture de
 * l'administration, vignette du catalogue.
 *
 * Ligne par ligne d'ordinaire, et l'ordre n'y change rien. Pour un alphabet joué dans l'ordre
 * (orderedPlay), couleur par couleur dans l'ordre de la palette : c'est l'ordre dans lequel le
 * joueur a forcément posé ses touches, donc le seul où ce qui déborde recouvre ce qu'il
 * recouvrira sur la feuille. Ligne par ligne, la couverture montrait un autre tableau — et un
 * moins bon — que celui qu'on obtient en jouant.
 */
export function paintOrder(
  target: readonly (readonly number[])[],
  a: Alphabet,
): [number, number][] {
  const cells: [number, number][] = [];
  for (let r = 0; r < target.length; r++)
    for (let c = 0; c < target[r].length; c++) if (target[r][c]) cells.push([r, c]);
  if (!a.orderedPlay) return cells;
  const rank = codeRank(a);
  // Array.prototype.sort est stable : à rang égal, l'ordre de lecture est gardé.
  return cells.sort((p, q) => rank[target[p[0]][p[1]]] - rank[target[q[0]][q[1]]]);
}

export type { Alphabet, CellDetail, Encoder, SourceGrid } from './types';
